import 'babel-polyfill';
import Vue from 'vue';
import vuetify from '@/plugins/vuetify';
import App from './App.vue';
import { createApolloProvider } from './graphql/apollo';
import router from './router';
import store from './store';
import VueHead from 'vue-head';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import VueGtag from 'vue-gtag';
import './plugins/vee-validate';
import './constants/js/global-mixin';
import './constants/js/filters';
import cssVars from 'css-vars-ponyfill';
import VueTelInput from 'vue-tel-input';
import VueCookies from 'vue-cookies';
import whitelabel from './whitelabel-config';
import FacebookAuthPlugin from './sso/facebook-auth';
import VueMeta from 'vue-meta';
import TooltipPlugin from './plugins/tooltip';
import mobileDeviceDetect from './plugins/mobile-device-detect';
import vueOrientation from './plugins/vue-orientation';
import ipadDetect from './plugins/ipad-detect';
import { isApolloError } from 'apollo-client';
import GradientButton from './components/guest/GradientButton.vue';
import FunkButton from './components/guest/FunkButton.vue';
import DefaultButton from './components/common/DefaultButton.vue';
import DirtyButton from './components/common/DirtyButton.vue';
import defineOmDesignSystemElements from './plugins/om-design-system';
import OMDesignWrapper from './components/common/OMDesignWrapper.vue';
import TextField from './components/common/components/TextField.vue';
import ListSelect from './components/common/components/ListSelect.vue';
import RadioSelect from './components/common/components/RadioSelect.vue';
import TabComponent from './components/common/components/TabComponent.vue';
import AlternativeButton from './components/common/components/AlternativeButton.vue';
import ListItems from './components/common/components/ListItems.vue';
import Checkbox from './components/common/components/Checkbox.vue';
defineOmDesignSystemElements();

if (typeof document !== 'undefined' && document.fonts && document.fonts.load) {
  document.fonts.load('24px "Material Symbols Outlined"').catch(() => {});
}

Vue.use(TooltipPlugin);

//iPad Detect
Vue.use(ipadDetect);

//Mobile Device Detect
Vue.use(mobileDeviceDetect);

//Mobile Device Detect
Vue.use(vueOrientation);

Vue.use(VueMeta, { refreshOnceOnNavigation: true });

// cookies
Vue.use(VueCookies);

// vue-tel-input
Vue.use(VueTelInput);

Vue.component('GradientButton', GradientButton);
Vue.component('FunkButton', FunkButton);
Vue.component('DefaultButton', DefaultButton);
Vue.component('DirtyButton', DirtyButton);
Vue.component('OMDesignWrapper', OMDesignWrapper);
Vue.component('TextField', TextField);
Vue.component('ListSelect', ListSelect);
Vue.component('RadioSelect', RadioSelect);
Vue.component('TabComponent', TabComponent);
Vue.component('AlternativeButton', AlternativeButton);
Vue.component('ListItems', ListItems);
Vue.component('Checkbox', Checkbox);
// css-vars-ponyfill
cssVars({});

// vue-gtag Google analytics
const brand = process.env.VUE_APP_BRAND;
if (brand !== 'oldmutual') {
  Vue.use(
    VueGtag,
    {
      config: { id: process.env.VUE_APP_GTM }, // your Google Analytics tracking ID
      pageTrackerTemplate: route => {
        return {
          page_title: route.name,
          page_path: route.path,
          page_location: window.location.href,
        };
      },
    },
    router
  );
}

Sentry.init({
  dsn: process.env.VUE_APP_SENTRY_DSN,
  ignoreErrors: [
    'UET is not defined',
    'Java object is gone',
    'webVitals.getCLS is not a function',
    `Cannot read properties of undefined (reading 'domInteractive')`,
    'window.SafeDKWebAppInterface.interceptXmlHttpRequest is not a function',
    `Cannot read properties of undefined (reading 'isProxied')`,
    `Member PerformanceObserverInit.entryTypes is required and must be an instance of sequence`,
    `onHide is not defined`,
    `Failed to fetch dynamically imported module: https://services.leadconnectorhq.com/appengine/cors/js/user-session.js`,
    `Failed to fetch dynamically imported module: https://www.googletagmanager.com/gtag/js?id=G-1X0XQRMB4F`,
    `Can't find variable: _AutofillCallbackHandler`,
    `Network error: Unexpected end of JSON input`,
    `Cannot read property 'domInteractive' of undefined`,
    `Failed to execute 'replaceSync' on 'CSSStyleSheet': @import rules are not allowed when creating stylesheet synchronously.`,
    `Failed to execute 'send' on 'XMLHttpRequest': Failed to load 'https://searchaggr-dre.dt.dbankcloud.com/search/api/v1/webtranslation_detect'.`,
    `l is undefined`,
    `undefined is not an object (evaluating 'l.isProxied')`,
    `Failed to fetch dynamically imported module: https://stcdn.leadconnectorhq.com/intl-tel-input/intlTelInput.min.js`,
    `e.setAttribute is not a function`,
    `window._META.IABInnerFrame`,
    `window.webkit.messageHandlers`,
    `window.JS_LANDING_PAGE_LOG_OBJ`,
    `xbrowser is not defined`,
    `swbrowser is not defined`,
    `VWO.visitor is not a function`,
    `Java bridge method invocation error`,
    `Importing a module script failed.`,
    /^Failed to fetch$/,
  ],
  denyUrls: [
    /script\.hotjar\.com/,
    /widgets\.leadconnectorhq\.com/,
    /googletagmanager\.com\/gtm\.js/,
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.VUE_APP_ENVIRONMENT,
  integrations:
    process.env.NODE_ENV === 'production' ? [new Sentry.Replay()] : [],
});
Sentry.configureScope(scope => {
  scope.setUser({ id: store.getters.userIdFromToken });
});

const ssoConfig = whitelabel(process.env.VUE_APP_BRAND).sso;

// vue-head
Vue.use(VueHead);

Vue.config.productionTip = false;

Vue.config.ignoredElements = [/^omk-/];

async function bootstrap() {
  const apolloProvider = await createApolloProvider();
  new Vue({
    render: h => h(App),
    apolloProvider,
    router,
    store,
    vuetify,
  }).$mount('#app');
}

bootstrap();
